import type { FetchQueryParams } from '@/components/paginated-datatable/types/paginatedDataTableTypes';
import {
  type AreaWithPartnerAndCaretakerCreateManyV1Dto,
  type AreaWithPartnerAndCaretakerDeleteManyV1Dto,
  type AreaWithPartnerAndCaretakerV1Dto,
  type ContractFilterInnerV2Dto,
  ContractingPartyToContractV1Dto,
  ContractsService,
  ContractTypesService,
  type ContractV1Dto,
  type ContractWithContractingPartiesAndActionsV1Dto,
  type CreateContractV1Dto,
  type PartnerAndCaretakerPartyPatchManyV1Dto,
  type PartnerAndCaretakerPartyPatchV1Dto,
  type PatchContractV1Dto,
  type ServiceContractV1Dto,
} from '@/openapi';
import { eventBus } from '@/configs/eventBus';

import type {
  AreaWithPartnerAndCaretaker,
  ContractSimple,
  ContractWithPartiesAndActionsSimple,
} from '../contractsTypes';

export function useContracts() {
  const fetchContract = async (id: string) => {
    try {
      return await ContractsService.contractControllerV1FindOneV1(id);
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const fetchContracts = async () => {
    try {
      return await ContractsService.contractControllerV1FindAllV1();
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const fetchContractsPaginated = ({
    actionsAvailable,
    customerId,
    customerGroupId,
    filter,
    limit,
    orderBy,
    page,
    partnerId,
    partnerGroupId,
    pointOfSaleId,
    searchString,
  }: FetchQueryParams<ContractFilterInnerV2Dto> & {
    actionsAvailable?: boolean;
    customerId?: string;
    customerGroupId?: string;
    partnerId?: string;
    partnerGroupId?: string;
    pointOfSaleId?: string;
  }) => {
    try {
      return ContractsService.contractControllerV2FindAllPaginatedV2(
        actionsAvailable,
        customerId,
        customerGroupId,
        partnerId,
        partnerGroupId,
        pointOfSaleId,
        filter,
        orderBy,
        searchString,
        page,
        limit,
      );
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const fetchContractsSimplePaginated = async ({
    actionsAvailable,
    customerId,
    customerGroupId,
    filter,
    limit,
    orderBy,
    page,
    partnerId,
    partnerGroupId,
    pointOfSaleId,
    searchString,
  }: FetchQueryParams<ContractFilterInnerV2Dto> & {
    actionsAvailable?: boolean;
    customerId?: string;
    customerGroupId?: string;
    partnerId?: string;
    partnerGroupId?: string;
    pointOfSaleId?: string;
  }) => {
    try {
      return await ContractsService.contractControllerV2FindAllSimplePaginatedV2(
        actionsAvailable,
        customerId,
        customerGroupId,
        partnerId,
        partnerGroupId,
        pointOfSaleId,
        filter,
        orderBy,
        searchString,
        page,
        limit,
      );
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const fetchContractsByCustomer = async (customerId: string) => {
    try {
      return await ContractsService.contractControllerV1FindManyByCustomerV1(customerId);
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const createContract = async (data: CreateContractV1Dto) => {
    try {
      const response = await ContractsService.contractControllerV1CreateV1(data);
      eventBus.emit('create');
      return response;
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const updateContract = async (id: string, data: PatchContractV1Dto) => {
    try {
      await ContractsService.contractControllerV1UpdatePartialV1(id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const deleteContract = async (ids: string) => {
    try {
      await ContractsService.contractControllerV1RemoveOneV1(ids);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const deleteAreaRelation = async (
    id: string,
    data: AreaWithPartnerAndCaretakerDeleteManyV1Dto,
  ) => {
    try {
      await ContractsService.contractAreaControllerV1UnassignAreasFromContractV1(id, data);
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const assignAreaRelation = async (
    id: string,
    data: AreaWithPartnerAndCaretakerCreateManyV1Dto,
  ) => {
    try {
      if (data.data.length === 0) return;
      await ContractsService.contractAreaControllerV1AssignAreasToContractV1(id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const updateContractingParties = async (
    id: string,
    data: PartnerAndCaretakerPartyPatchManyV1Dto,
  ) => {
    try {
      if (data.data.length === 0) return;
      await ContractsService.contractAreaControllerV1UpdatePartialContractingPartiesV1(id, data);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const deleteContracts = async (ids: string[]) => {
    try {
      await ContractsService.contractControllerV1RemoveManyV1({ values: ids });
      eventBus.emit('delete');
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const assignDocumentRelation = async (id: string, documentId: string) => {
    try {
      await ContractsService.contractToDocumentControllerV1LinkDocumentV1(id, documentId);
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  const unassignManyDocumentRelation = async (id: string, documentIds: string[]) => {
    try {
      await ContractsService.contractToDocumentControllerV1UnlinkManyDocumentsV1(id, {
        values: documentIds,
      });
      eventBus.emit('update');
    } catch (err) {
      console.log(err);
      throw err;
    }
  };

  return {
    fetchContract,
    fetchContracts,
    fetchContractsPaginated,
    fetchContractsSimplePaginated,
    fetchContractsByCustomer,
    createContract,
    updateContract,
    deleteContract,
    deleteContracts,
    deleteAreaRelation,
    assignAreaRelation,
    updateContractingParties,
    assignDocumentRelation,
    unassignManyDocumentRelation,
  };
}

export function useContractTypes() {
  const fetchContractType = async (contractTypeId: string) => {
    try {
      return await ContractTypesService.contractTypeControllerV1FindOneV1(contractTypeId);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchContractTypes = async (category?: 'Rental' | 'Service') => {
    try {
      return await ContractTypesService.contractTypeControllerV1FindAllV1(category);
    } catch (err) {
      console.log(err);
    }
  };

  return {
    fetchContractType,
    fetchContractTypes,
  };
}

export function useContractsAreasWithPartnerAndCaretakerConverter() {
  const getAreasWithPartnerAndCaretakerFromContract = (
    contract: ContractV1Dto | ServiceContractV1Dto,
  ): AreaWithPartnerAndCaretaker[] => {
    const areasWithPartnerAndCaretaker: AreaWithPartnerAndCaretaker[] = [];

    const defaultAreaWithPartnerAndCaretaker: AreaWithPartnerAndCaretaker = {
      areaId: '',
      partnerPartyId: '',
      partnerGroupId: '',
      partnerRole: ContractingPartyToContractV1Dto.role.PROPERTY_LANDLORD,
      partnerId: '',
    };

    if (!contract.areasToContracts || contract.areasToContracts.length === 0) {
      return [defaultAreaWithPartnerAndCaretaker];
    }

    contract.areasToContracts.forEach((areaToContract) => {
      const partnerParty = contract.contractingParties?.find(
        (x) => x.partner && x.areaId === areaToContract.area.id,
      );

      const caretakerParty = contract.contractingParties?.find(
        (x) => x.caretaker && x.areaId === areaToContract.area.id,
      );

      const areaWithPartnerAndCaretaker: AreaWithPartnerAndCaretaker = {
        areaId: areaToContract.area.id,
        partnerPartyId: partnerParty?.id ?? '',
        partnerGroupId: partnerParty?.partner?.partnerGroupId ?? '',
        partnerRole: partnerParty?.role ?? ContractingPartyToContractV1Dto.role.PROPERTY_LANDLORD,
        partnerId: partnerParty?.partner?.id ?? '',
        caretakerPartyId: caretakerParty?.id,
        caretakerPartnerGroupId: caretakerParty?.caretaker?.partnerGroupId ?? undefined,
        caretakerRole: caretakerParty?.role,
        caretakerId: caretakerParty?.caretaker?.id,
      };
      areasWithPartnerAndCaretaker.push(areaWithPartnerAndCaretaker);
    });

    return areasWithPartnerAndCaretaker;
  };

  const convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerDeleteManyDto = (
    data: AreaWithPartnerAndCaretaker,
  ): AreaWithPartnerAndCaretakerDeleteManyV1Dto => {
    const {
      partnerGroupId: _partnerGroupId,
      caretakerPartnerGroupId: _caretakerPartnerGroupId,
      partnerRole: _partnerRole,
      caretakerRole: _caretakerRole,
      partnerPartyId: _partnerPartyId,
      caretakerPartyId: _caretakerPartyId,
      partnerId: _partnerId,
      caretakerId: _caretakerId,
      ...rest
    } = data;
    return { areaIds: [rest.areaId] };
  };

  const convertAreaWithPartnerAndCaretakerToPartnerAndCaretakerPartyPatchManyDto = (
    data: AreaWithPartnerAndCaretaker[],
  ): PartnerAndCaretakerPartyPatchManyV1Dto => {
    const result = data.map((item: AreaWithPartnerAndCaretaker) => {
      const dto: PartnerAndCaretakerPartyPatchV1Dto = {};

      dto.partner = item.partnerPartyId
        ? { contractingPartyId: item.partnerPartyId, id: item.partnerId, role: item.partnerRole }
        : undefined;
      dto.caretaker = item.caretakerPartyId
        ? {
            contractingPartyId: item.caretakerPartyId,
            id: item.caretakerId,
            role: item.caretakerRole,
          }
        : undefined;

      return dto;
    });

    return { data: result };
  };

  const convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerCreateManyDto = (
    data: AreaWithPartnerAndCaretaker[],
  ): AreaWithPartnerAndCaretakerCreateManyV1Dto => {
    const result = data.map((item: AreaWithPartnerAndCaretaker) => {
      const {
        partnerGroupId: _partnerGroupId,
        caretakerPartnerGroupId: _caretakerPartnerGroupId,
        partnerPartyId: _partnerPartyId,
        caretakerPartyId: _caretakerPartyId,
        ...rest
      } = item;

      return {
        ...rest,
        caretakerId: item.caretakerId ?? item.partnerId,
        caretakerRole: ContractingPartyToContractV1Dto.role.PROPERTY_MANAGER,
      };
    });
    return { data: result };
  };

  const convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerDto = (
    data: AreaWithPartnerAndCaretaker,
  ): AreaWithPartnerAndCaretakerV1Dto => {
    const {
      partnerGroupId: _partnerGroupId,
      caretakerPartnerGroupId: _caretakerPartnerGroupId,
      partnerPartyId: _partnerPartyId,
      caretakerPartyId: _caretakerPartyId,
      ...rest
    } = data;
    return {
      ...rest,
      caretakerId: data.caretakerId ?? data.partnerId,
      caretakerRole: ContractingPartyToContractV1Dto.role.PROPERTY_MANAGER,
    };
  };

  return {
    getAreasWithPartnerAndCaretakerFromContract,
    convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerDeleteManyDto,
    convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerCreateManyDto,
    convertAreaWithPartnerAndCaretakerToPartnerAndCaretakerPartyPatchManyDto,
    convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerDto,
  };
}

export function useContractsConverter() {
  const {
    getAreasWithPartnerAndCaretakerFromContract,
    convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerDto,
  } = useContractsAreasWithPartnerAndCaretakerConverter();

  const convertContractDtoToContractSimpleV2 = (data: ContractV1Dto): ContractSimple => {
    const { contractType, ...res } = data;

    const customerParty = data.contractingParties?.find((x) => x.customer);

    return {
      ...res,
      contractType: contractType.name,
      customerGroupId: customerParty?.customer?.customerGroupId,
      customerId: customerParty?.customer?.id ?? '',
      customerRole: customerParty?.role ?? ContractingPartyToContractV1Dto.role.PROPERTY_TENANT,
      areasWithPartnerAndCaretaker: getAreasWithPartnerAndCaretakerFromContract(data),
    };
  };

  const convertContractDtoToContractSimple = (data: ContractV1Dto): ContractSimple => {
    const { createdAt: _createdAt, modifiedAt: _modifiedAt, contractType, ...res } = data;

    const customerParty = data.contractingParties?.find((x) => x.customer);

    return {
      ...res,
      contractType: contractType.name,
      customerGroupId: customerParty?.customer?.customerGroupId,
      customerId: customerParty?.customer?.id ?? '',
      customerRole: customerParty?.role ?? ContractingPartyToContractV1Dto.role.PROPERTY_TENANT,
      areasWithPartnerAndCaretaker: getAreasWithPartnerAndCaretakerFromContract(data),
    };
  };

  const convertContractDtoListToContractSimpleList = (data: ContractV1Dto[]): ContractSimple[] => {
    return data.map(convertContractDtoToContractSimple);
  };

  const convertContractDtoListToContractSimpleListV2 = (
    data: ContractV1Dto[],
  ): ContractSimple[] => {
    return data.map(convertContractDtoToContractSimpleV2);
  };

  const convertContractWithPartiesAndActionsDtoToContractWithPartiesAndActionSimple = (
    data: ContractWithContractingPartiesAndActionsV1Dto,
  ): ContractWithPartiesAndActionsSimple => {
    const { createdAt: _createdAt, modifiedAt: _modifiedAt, contractType, ...res } = data;

    const customerParty = data.contractingParties?.find((x) => x.customer);

    return {
      ...res,
      contractType: contractType.name,
      customerGroupId: customerParty?.customer?.customerGroupId,
      customerId: customerParty?.customer?.id ?? '',
      customerRole: customerParty?.role ?? ContractingPartyToContractV1Dto.role.PROPERTY_TENANT,
    };
  };

  const convertContractWithPartiesAndActionDtoListToContractWithPartiesAndActionsSimpleList = (
    data: ContractWithContractingPartiesAndActionsV1Dto[],
  ): ContractWithPartiesAndActionsSimple[] => {
    return data.map(convertContractWithPartiesAndActionsDtoToContractWithPartiesAndActionSimple);
  };

  const convertContractSimpleToCreateContractDto = (data: ContractSimple): CreateContractV1Dto => {
    const {
      version: _version,
      id: _id,
      customerGroupId: _customerGroupId,
      areasToContracts: _areasToContracts,
      contractingParties: _contractingParties,
      contactPersonToContract: _contactPersonToContract,
      contractClauses: _contractClauses,
      contractAction: _contractAction,
      definiteActionEnd: _definiteActionEnd,
      actionStart: _actionStart,
      supposedActionEnd: _supposedActionEnd,
      nextPossibleContractEnd: _nextPossibleContractEnd,
      lastPossibleContractEnd: _lastPossibleContractEnd,
      payments: _payments,
      operatingCostInvoices: _operatingCostInvoices,
      areasWithPartnerAndCaretaker,
      ...res
    } = data;
    return {
      ...res,
      areaWithPartnerAndCaretaker: areasWithPartnerAndCaretaker.map(
        convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerDto,
      ),
    };
  };

  const convertContractSimpleToUpdateContractDto = (data: ContractSimple): PatchContractV1Dto => {
    const {
      id: _id,
      contractType: _contractType,
      contractClauses: _contractClauses,
      customerGroupId: _customerGroupId,
      areasToContracts: _areasToContracts,
      contractingParties: _contractingParties,
      contactPersonToContract: _contactPersonToContract,
      contractAction: _contractAction,
      definiteActionEnd: _definiteActionEnd,
      actionStart: _actionStart,
      supposedActionEnd: _supposedActionEnd,
      nextPossibleContractEnd: _nextPossibleContractEnd,
      lastPossibleContractEnd: _lastPossibleContractEnd,
      payments: _payments,
      documents: _documents,
      operatingCostInvoices: _operatingCostInvoices,
      contractTypeId: _contractTypeId,
      areasWithPartnerAndCaretaker: _areasWithPartnerAndCaretaker,
      ...res
    } = data;
    return res;
  };

  return {
    convertContractDtoToContractSimple,
    convertContractDtoListToContractSimpleList,
    convertContractDtoListToContractSimpleListV2,
    convertContractSimpleToCreateContractDto,
    convertContractSimpleToUpdateContractDto,
    convertContractWithPartiesAndActionDtoListToContractWithPartiesAndActionsSimpleList,
  };
}

export function useContractsValidation() {
  const validateContractAreas = (
    areasWithPartnerAndCaretaker: AreaWithPartnerAndCaretaker[],
    setFieldError: (field: string, message: string) => void,
    t: (key: string) => string,
  ) => {
    areasWithPartnerAndCaretaker?.forEach((area, index) => {
      const { caretakerId, caretakerRole, areaId, partnerId, partnerRole } = area;
      const oneFilled = !!caretakerId || !!caretakerRole;
      const bothFilled = !!caretakerId && !!caretakerRole;

      if (!areaId || !partnerId || !partnerRole) {
        if (!areaId) {
          setFieldError(`areasWithPartnerAndCaretaker[${index}].areaId`, t('errors.required'));
        }
        if (!partnerId) {
          setFieldError(`areasWithPartnerAndCaretaker[${index}].partnerId`, t('errors.required'));
        }
        if (!partnerRole) {
          setFieldError(`areasWithPartnerAndCaretaker[${index}].partnerRole`, t('errors.required'));
        }
      }

      if (oneFilled && !bothFilled) {
        if (!caretakerId) {
          setFieldError(`areasWithPartnerAndCaretaker[${index}].caretakerId`, t('errors.required'));
        }
        if (!caretakerRole) {
          setFieldError(
            `areasWithPartnerAndCaretaker[${index}].caretakerRole`,
            t('errors.required'),
          );
        }
      }
    });
  };

  return { validateContractAreas };
}
